import React from 'react'
import {
    Typography,
    TextField,
    Stack,
    Button,
    Switch,
    MenuItem,
    Select
} from '@mui/material'
import { VpnKeyOutlined, DeleteOutlined, MailOutline } from '@mui/icons-material'
import { AxiosWithAuth } from './authenticationService'
import { ROLE_MAPPINGS, PART_FULLTIME_MAPPINGS, OPEN_CLOSE_MAPPINGS } from '../constants'
import ButtonWithConfirmPrompt from '../Components/ButtonWithConfirmPrompt'
import { PATHS } from '../constants'
import { isMobile } from './isMobile'
import { validateHiraganaKatakana, 
    validateKanjiHiraganaKatakana, 
    validateEmail, 
    validatePhone,
    validateEmpCode 
} from './fieldValidators'

// Common field validators and error messages
export const fieldValidators = {
    name_last: validateHiraganaKatakana,
    name_first: validateHiraganaKatakana,
    kanji_last: validateKanjiHiraganaKatakana,
    kanji_first: validateKanjiHiraganaKatakana,
    name_middle: validateHiraganaKatakana,
    nickname: validateHiraganaKatakana,
    email: validateEmail,
    phone: validatePhone,
    emp_cd: validateEmpCode,
    hourly_wage: validateEmpCode
}

export const fieldErrorText = {
    name_last: '全角ひらがな・全角カタカナ',
    name_first: '全角ひらがな・全角カタカナ',
    kanji_last: '全角漢字・全角ひらがな・全角カタカナ',
    kanji_first: '全角漢字・全角ひらがな・全角カタカナ',
    name_middle: '全角ひらがな・全角カタカナ',
    nickname: '全角ひらがな・全角カタカナ',
    email: '半角英数字',
    phone: '半角数字',
    emp_cd: '半角数字',
    hourly_wage: '半角数字'
}

// Common text field component
export const getTextField = (label, key, info, setInfo, errors, setErrors, disable = false) => {
    return (
        <TextField
            label={label}
            value={info[key]}
            onChange={e => {
                if (!fieldValidators[key](e.target.value)) {
                    setErrors({ ...errors, [key]: fieldErrorText[key] })
                } else {
                    setErrors({ ...errors, [key]: '' })
                }
                setInfo({ ...info, [key]: e.target.value })            
            }}
            sx={{ width: isMobile() ? '90%' : '50%' }}
            error={errors[key]}
            helperText={errors[key]}
            disabled={disable}
            size={isMobile() ? 'small' : 'medium'}
        />
    )
}

// Common store text field component
export const getStoreTextField = (label, key, info, storeList, setOpenEditStores) => {
    const selectedStores = storeList.filter(store => info[key]?.includes(store.id))
    return (
        <TextField
            label={label}
            value={selectedStores.map(store => store.name).join(', ')}
            onClick={() => {
                setOpenEditStores(true)
            }}
            sx={{ width: isMobile() ? '90%' : '50%' }}
        />
    )
}

// Common number field component
export const getNumberField = (label, key, info, setInfo, disable = false) => {
    return (
        <TextField
            label={label}
            value={info[key]}
            type="number"
            sx={{ width: '50%' }}
            onChange={e => {
                setInfo({ ...info, [key]: e.target.value })
            }}
            disabled={disable}
        />
    )
}

// Common employment type select component
export const getEmploymentTypeSelect = (info, setInfo, canEditTargetUser) => {
    return (
        <Select
            id="eType"
            label="雇用形態"
            placeholder="選択してください"
            value={info.e_type || ''}
            onChange={event =>
                canEditTargetUser() && setInfo({ ...info, e_type: event.target.value })
            }
            sx={{ width: isMobile() ? '90%' : '49%' }}
            disabled={!canEditTargetUser()}
        >
            {Object.keys(PART_FULLTIME_MAPPINGS).map(key => (
                <MenuItem key={key} value={key}>
                    {PART_FULLTIME_MAPPINGS[key]}
                </MenuItem>
            ))}
        </Select>
    )
}

// Common employment card component
export const EmploymentCard = ({ 
    employment, 
    employments, 
    setEmployments, 
    info, 
    setSnackbarMessage 
}) => {
    return (
        <div>
            <Typography variant="header1" sx={{ fontSize: '20px' }}>
                {employment.store.name}
            </Typography>
            <div style={{ marginTop: '.5rem', borderBottom: '1px solid #e0e0e0' }} />
            <Stack spacing={2} style={{ margin: '1rem' }}>
                <TextField
                    select
                    id="kitchen/hall"
                    label="勤務タイプ"
                    variant="standard"
                    placeholder="選択してください"
                    value={employment.role || ''}
                    onChange={event =>
                        setEmployments(
                            employments.map(emp =>
                                emp.id === employment.id ? { ...emp, role: event.target.value } : emp
                            )
                        )
                    }
                >
                    {Object.keys(ROLE_MAPPINGS).map(key => (
                        <MenuItem key={key} value={key}>
                            {ROLE_MAPPINGS[key]}
                        </MenuItem>
                    ))}
                </TextField>
                <TextField
                    select
                    id="open/close"
                    label="開閉担当者"
                    variant="standard"
                    placeholder="選択してください"
                    value={employment.open_close_duty || ''}
                    onChange={event =>
                        setEmployments(
                            employments.map(emp =>
                                emp.id === employment.id ? { ...emp, open_close_duty: event.target.value } : emp
                            )
                        )
                    }
                >
                    {Object.keys(OPEN_CLOSE_MAPPINGS).map(key => (
                        <MenuItem key={key} value={key}>
                            {' '}
                            {OPEN_CLOSE_MAPPINGS[key]}{' '}
                        </MenuItem>
                    ))}
                </TextField>
                <TextField
                    select
                    id="rank"
                    label="ランク"
                    variant="standard"
                    placeholder="選択してください"
                    value={employment.rank || ''}
                    onChange={event =>
                        setEmployments(
                            employments.map(emp =>
                                emp.id === employment.id ? { ...emp, rank: event.target.value } : emp
                            )
                        )
                    }
                >
                    {['A', 'B', 'T'].map(key => (
                        <MenuItem key={key} value={key}>
                            {key}
                        </MenuItem>
                    ))}
                </TextField>
            </Stack>
            <div
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    margin: '0 1rem'
                }}
            >
                <Typography variant="subtitle1">シフト更新権限</Typography>
                <Switch
                    checked={employment.can_edit_shift}
                    onChange={event => {
                        if (employment.support_staff) {
                            setSnackbarMessage('サポートスタッフはシフト更新権限を持つことができません。')
                            return
                        }
                        setEmployments(
                            employments.map(emp =>
                                emp.id === employment.id ? { ...emp, can_edit_shift: event.target.checked } : emp
                            )
                        )
                    }}
                />
            </div>
            <Typography
                variant="body2"
                style={{
                    margin: '0 1rem',
                    marginBottom: '1rem',
                    marginRight: '2rem',
                    color: 'grey'
                }}
            >
                シフト更新権限があるユーザーはシフトを作成することができます。
            </Typography>
            <div
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    margin: '0 1rem'
                }}
            >
                <Typography variant="subtitle1">マネージャー</Typography>
                <Switch
                    checked={employment.is_manager}
                    disabled={info.e_type != 'full-time'}
                    onChange={event => {
                        if (employment.support_staff) {
                            setSnackbarMessage('サポートスタッフはマネージャーにできません。')
                            return
                        }
                        setEmployments(
                            employments.map(emp =>
                                emp.id === employment.id ? { ...emp, is_manager: event.target.checked } : emp
                            )
                        )
                    }}
                />
            </div>
            <Typography
                variant="body2"
                style={{
                    margin: '0 1rem',
                    marginBottom: '1rem',
                    marginRight: '2rem',
                    color: 'grey'
                }}
            >
                正社員のみマネージャーにできます。
            </Typography>
            <div
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    margin: '0 1rem',
                    marginBottom: '3rem'
                }}
            >
                <Typography variant="subtitle1">サポートスタッフ</Typography>
                <Switch
                    checked={employment.support_staff}
                    onChange={event => {
                        const checked = event.target.checked
                        setEmployments(
                            employments.map(emp =>
                                emp.id === employment.id ? { ...emp, support_staff: checked, 
                                                            is_manager: checked ? false : emp.is_manager, 
                                                            can_edit_shift: checked ? false : emp.can_edit_shift } : emp
                            )
                        )
                    }}
                />
            </div>
        </div>
    )
}

// Common admin/manager buttons component
export const AdminManagerButtons = ({ 
    info, 
    userId, 
    setSnackbarMessage, 
    fetch, 
    setNewPass, 
    setIsLoading, 
    history, 
    employmentId, 
    isAdmin 
}) => {
    return (
        <>
            {!info.confirmed_at && (
                <Stack direction="row" spacing={2}>
                    <ButtonWithConfirmPrompt
                        component={
                            <Button variant="outlined" color="paloBlue" sx={{ mx: '1rem', mb: '1rem' }}>
                                <MailOutline sx={{ mr: '.5rem' }} />
                                メールアドレスを代理で確認
                            </Button>
                        }
                        title="メールアドレスを代理で確認"
                        content="このユーザーのメールアドレスを代理で確認しますか？"
                        confirmText="確認する"
                        handleConfirm={() => {
                            AxiosWithAuth.post('/confirmUser/', { id: info.id }).then(res => {
                                console.log(res.data)
                                if (res.status == 200) {
                                    setSnackbarMessage('メールアドレスの本人確認が完了しました。')
                                    fetch()
                                }
                            })
                        }}
                    />
                </Stack>
            )}
            <Stack direction="row" spacing={2}>
                <ButtonWithConfirmPrompt
                    component={
                        <Button variant="outlined" color="paloBlue" sx={{ mx: '1rem', mb: '1rem' }}>
                            <VpnKeyOutlined sx={{ mr: '.5rem' }} />
                            このユーザーのパスワードをリセット
                        </Button>
                    }
                    title="パスワードのリセット"
                    content="このユーザーのパスワードをリセットしますか？"
                    confirmText="リセットする"
                    handleConfirm={() => {
                        AxiosWithAuth.post('/resetPassword', { id: info.id }).then(res => {
                            if (res.status == 200) {
                                setNewPass(res.data.password)
                            }
                        })
                    }}
                />
            </Stack>
            <Stack direction="row" spacing={2}>
                <ButtonWithConfirmPrompt
                    component={
                        <Button variant="outlined" color="error" sx={{ mx: '1rem', mb: '2rem' }} disabled={info.is_deactivated}>
                            <DeleteOutlined sx={{ mr: '.5rem' }} />
                            このユーザーのアカウントを無効にする
                        </Button>
                    }
                    title="ユーザーの無効化"
                    content="アカウントを無効にすると、このユーザーはログインできなくなります。このユーザーを無効にしてもよろしいですか？"
                    confirmText="無効にする"
                    handleConfirm={() => {
                        AxiosWithAuth.post('/users/deactivate/' + userId).then(res => {
                            if(res.status == 200 && res.data.message === "User deactivated") {
                                setSnackbarMessage('ユーザーを無効にしました。')
                                fetch()
                            } else {
                                setSnackbarMessage('ユーザーを無効にできませんでした。')
                            }
                        })
                    }}
                />
            </Stack>    
            {isAdmin && (
                <Stack direction="row" spacing={2}>
                    <ButtonWithConfirmPrompt
                        component={
                            <Button variant="outlined" color="error" sx={{ mx: '1rem', mb: '5rem' }}>
                                <DeleteOutlined sx={{ mr: '.5rem' }} />
                                このユーザーのアカウントを削除
                            </Button>
                        }
                        title="ユーザーの削除"
                        content="アカウントを削除すると過去のデータはすべて削除され、復元することはできません。このユーザーのアカウントを完全に削除してもよろしいですか？"
                        confirmText="削除する"
                        handleConfirm={() => {
                            setIsLoading(true)
                            const currPath = history.location.pathname
                            const newPath = currPath.includes('admin') ? PATHS.adminUserManagements : PATHS.userManagementStores + employmentId + '/deleted'

                            AxiosWithAuth.delete('/users/' + userId).then(res => {
                                res.status == 204 &&
                                    history.push({ pathname: newPath, state: { deleted: userId } })
                                setIsLoading(false)
                            })
                        }}
                    />
                </Stack>
            )}
        </>
    )
}

// Common save handler
export const handleSave = (employments, originalEmployments, info, originalInfo, errors, setErrors, setSnackbarMessage, userId) => {
    if (Object.values(errors).some(e => e)) {
        return
    }
    
    // Save employment changes
    employments.forEach((e, i) => {
        const originalEmployment = originalEmployments[i]
        const updatedEmployment = Object.keys(e).reduce((acc, key) => {
            if (e[key] !== originalEmployment[key]) {
                acc[key] = e[key]
            }
            return acc
        }, {})
        if (Object.keys(updatedEmployment).length) {
            AxiosWithAuth.put('/employments/' + e.id, {
                ...updatedEmployment
            })
                .then(res => {
                    res.status === 200 && setSnackbarMessage('変更を保存しました。')
                })
                .catch(error => {
                    const errors = {}
                    for (const [key, value] of Object.entries(error?.response?.data)) {
                        errors[key] = value.join(' ')
                    }
                    setErrors(errors)
                })
        }
    })
    
    // Save user info changes
    const updatedInfo = Object.keys(info).reduce((acc, key) => {
        if (info[key] !== originalInfo[key]) {
            acc[key] = info[key]
        }
        return acc
    }, {})
    if (Object.keys(updatedInfo).length) {
        AxiosWithAuth.put('/users/' + userId, {
            ...updatedInfo
        })
            .then(res => {
                if (res.status === 200) {
                    setSnackbarMessage('変更を保存しました。');
                }
            })
            .catch(error => {
                const errors = {}
                for (const [key, value] of Object.entries(error?.response?.data)) {
                    errors[key] = value.join(' ')
                }
                setErrors(errors)
            })
    }
}

// Common fetch function
export const fetchUserData = async (userId, userEmpId, setInfo, setOriginalInfo, setEmployments, setOriginalEmployments, setStoreList) => {
    const userInfo = await AxiosWithAuth.get('/users/' + userId)
    const employmentInfo = userEmpId !== 'noEmployment' ? await AxiosWithAuth.get('/employments/' + userEmpId) : {}
    userInfo.data.employments?.length && (userInfo.data.store_ids = userInfo.data.employments.map(e => e.store_id))
    const userEmploymentsInfo = await AxiosWithAuth.get('/employments/?user_id=' + userId)
    await AxiosWithAuth.get('/stores').then(res => setStoreList(res.data))
    setEmployments(userEmploymentsInfo.data?.sort((a, b) => a.id - b.id))
    setOriginalEmployments(userEmploymentsInfo.data?.sort((a, b) => a.id - b.id))
    const infoToAdd = userEmpId === 'noEmployment' ? userEmploymentsInfo?.data[0] : employmentInfo.data
    setInfo({ ...infoToAdd, ...userInfo.data })
    setOriginalInfo({ ...infoToAdd, ...userInfo.data })
}

// Common editing state effect
export const useEditingState = (info, originalInfo, employments, originalEmployments, setEditing) => {
    React.useEffect(() => {
        if (info && originalInfo) {
            const updated = Object.keys(info).some(key => info[key] !== originalInfo[key])
            const updatedEmployments = employments.some((e, i) => {
                const originalEmployment = originalEmployments?.[i]
                if (!originalEmployment) return false
                return Object.keys(e).some(key => e[key] !== originalEmployment[key])
            })
            if (updated || updatedEmployments) {
                setEditing(true)
            } else {
                setEditing(false)
            }
        }
    }, [info, originalInfo, employments, originalEmployments])
}
